import { ButtonSpeedDial, FloatingLoader } from "@/components";
import { useDebounce } from "@/hooks/useDebounce";
import useSyncMutation from "@/hooks/useSyncMutation";
import { getGroupsQueryOptions } from "@/queries";
import { ChatListSearchSchema } from "@/schemas";
import { useSyncStore } from "@/stores/sync";
import { useSuspenseInfiniteQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useVirtualizer } from "@tanstack/react-virtual";
import { zodSearchValidator } from "@tanstack/router-zod-adapter";
import { useEffect, useMemo, useRef } from "react";
import AppBarChat from "../-components/AppBar";
import ChatItem from "../-components/ChatItem";
import EmptyChat from "../-components/Empty";
import { getGroupsLoader } from "../-funtions/loader/group";

export const Route = createFileRoute("/dashboard/chat/group/")({
	validateSearch: zodSearchValidator(ChatListSearchSchema),
	loader: ({ context }) => getGroupsLoader(context),
	component: RouteComponent,
});

function RouteComponent() {
	const navigate = Route.useNavigate();
	const searchQ = Route.useSearch({
		select: (state: ChatListSearchSchema) => state.q ?? "",
	});
	const scrollRef = useRef<HTMLDivElement>(null);
	const debounceSearch = useDebounce(searchQ, 300);
	const infiniteGroups = useSuspenseInfiniteQuery(getGroupsQueryOptions());
	const groups = useMemo(() => {
		const items = infiniteGroups.data;
		if (debounceSearch) {
			return items.filter((group) =>
				group.name.toLowerCase().includes(debounceSearch.toLowerCase()),
			);
		}
		return items;
	}, [debounceSearch, infiniteGroups.data]);
	const isLoading = useMemo(
		() =>
			infiniteGroups.isLoading ||
			infiniteGroups.isFetching ||
			infiniteGroups.isFetchingNextPage,
		[
			infiniteGroups.isLoading,
			infiniteGroups.isFetching,
			infiniteGroups.isFetchingNextPage,
		],
	);

	const groupSyncLastUnix = useSyncStore((s) => s.groupSyncLastUnix);
	const { groupSync } = useSyncMutation();

	const onClickAdd = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();
		return navigate({
			to: "/dashboard/chat/group/create",
		});
	};

	const groupsVirtualizer = useVirtualizer({
		count: groups.length,
		gap: 12,
		overscan: 5,
		paddingStart: 16,
		paddingEnd: groups.length ? 295 : undefined,
		getScrollElement: () => scrollRef.current,
		estimateSize: () => 68,
	});

	const groupsVirtualizerItems = groupsVirtualizer.getVirtualItems();

	const isEmpty = useMemo(
		() => !isLoading && !searchQ && !groups.length,
		[isLoading, groups, searchQ],
	);

	const isEmptySearch = useMemo(
		() => !isLoading && !!searchQ && !groups.length,
		[isLoading, groups, searchQ],
	);

	const onClickSync = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();
		return groupSync.mutate({
			last_requested_at: groupSyncLastUnix,
		});
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		if (!groupSync.isPending) {
			groupSync.mutate({
				last_requested_at: 0,
			});
		}
	}, []);

	return (
		<>
			<AppBarChat />
			<div ref={scrollRef} className="relative h-full w-full overflow-y-auto">
				<FloatingLoader open={isLoading} />
				{!isEmpty || !isEmptySearch ? (
					<div
						className="flex flex-col gap-3"
						style={{
							height: groupsVirtualizer.getTotalSize(),
						}}
					>
						{groupsVirtualizerItems.map((groupVirtualizerItem) => {
							const group = groups[groupVirtualizerItem.index];
							return (
								<Link
									key={groupVirtualizerItem.key}
									to="/dashboard/chat/group/$id/$channelName"
									params={{ id: group.id, channelName: group.channel_name }}
									className="container absolute top-0 left-0"
									style={{
										height: groupVirtualizerItem.size,
										transform: `translateY(${groupVirtualizerItem.start}px)`,
									}}
								>
									<ChatItem
										type="group"
										title={group.name}
										unread={group.unread}
										lastMessage={group.last_message}
										avatar={group.avatar}
									/>
								</Link>
							);
						})}
					</div>
				) : null}
				{isEmptySearch ? (
					<EmptyChat title={`No results found for '${debounceSearch}'`} />
				) : null}
				{isEmpty ? (
					<EmptyChat
						title="Connect, learn, and grow in this channel!"
						addLabel="Create Group"
						onClickAdd={onClickAdd}
					/>
				) : null}
			</div>
			<ButtonSpeedDial
				mb={0}
				bottom={0}
				syncProcessing={groupSync.isPending}
				onClickAdd={onClickAdd}
				onClickSync={onClickSync}
			/>
		</>
	);
}
